import { Link } from "react-router-dom";
import { clsx } from "clsx";
import type { Article } from "../api";
import { formatDate } from "../utils/date";

const CATEGORY_LABELS: Record<string, string> = {
  policy_regulation: "政策监管",
  product_update:    "产品动态",
  market_trend:      "市场趋势",
  company_news:      "公司资讯",
  risk_event:        "风险事件",
  technology:        "技术创新",
};

/* All categories use neutral badge — no per-category colour differentiation */
const CATEGORY_DEFAULT_BADGE = "text-ink-tertiary bg-surface-sunken";

const SOURCE_TYPE_CLS: Record<string, string> = {
  official_gov: "text-ink-secondary font-semibold",
  official_pr:  "text-ink-secondary",
  news:         "text-ink-tertiary",
};

const SENTIMENT_DOT: Record<string, string> = {
  positive: "bg-positive",
  negative: "bg-negative",
  neutral:  "bg-ink-faint",
};

const IMPORTANCE_BADGE: Record<number, { label: string; cls: string }> = {
  5: { label: "紧要", cls: "text-ink bg-surface-sunken border-border-strong font-bold" },
  4: { label: "重要", cls: "text-ink-secondary bg-surface-sunken border-border font-semibold" },
  3: { label: "关注", cls: "text-ink-tertiary bg-surface-sunken border-border" },
};

export function ArticleCard({ article }: { article: Article }) {
  const score = article.importance_score ?? 0;
  const imp   = score >= 3 ? IMPORTANCE_BADGE[Math.min(score, 5)] : null;

  return (
    <div className={clsx(
      "group px-5 py-4 transition-colors duration-100",
      "hover:bg-surface"
    )}>
      {/* Meta row */}
      <div className="flex items-center gap-2 mb-2 text-2xs">
        {imp && (
          <span className={clsx(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold border shrink-0",
            imp.cls
          )}>
            <span className="w-1 h-1 rounded-full bg-current opacity-70" />
            {imp.label}
          </span>
        )}
        <span className={clsx("font-medium shrink-0", SOURCE_TYPE_CLS[article.source_type ?? "news"] ?? "text-ink-tertiary")}>
          {article.source_name}
        </span>
        {article.published_at && (
          <span className="text-ink-faint font-mono tabular-nums shrink-0">
            {formatDate(article.published_at)}
          </span>
        )}
        {article.category && (
          <span className={clsx(
            "px-1.5 py-0.5 rounded text-2xs font-medium shrink-0",
            CATEGORY_DEFAULT_BADGE
          )}>
            {CATEGORY_LABELS[article.category] ?? article.category}
          </span>
        )}
        <span className="flex-1" />
        {article.sentiment && (
          <span
            className={clsx("w-1.5 h-1.5 rounded-full shrink-0", SENTIMENT_DOT[article.sentiment] ?? "bg-ink-faint")}
            title={article.sentiment === "positive" ? "正面" : article.sentiment === "negative" ? "负面" : "中性"}
          />
        )}
      </div>

      {/* Title */}
      <Link
        to={`/article/${article.id}`}
        className="block text-[15px] font-semibold leading-7 text-ink group-hover:text-primary-600 dark:group-hover:text-primary-400 line-clamp-2 transition-colors duration-100"
      >
        {article.clean_title || article.title}
      </Link>

      {/* Summary */}
      {article.summary && (
        <p className="mt-1.5 text-sm leading-6 text-ink-secondary line-clamp-1">
          {article.summary}
        </p>
      )}

      {/* Credit relevance + Risk transmission — typographic footnote style */}
      {(article.credit_relevance || article.risk_transmission) && (
        <div className="mt-2 space-y-1">
          {article.credit_relevance && (
            <p className="text-[11px] leading-[1.55] text-ink-tertiary line-clamp-1">
              <span className="font-semibold text-ink-secondary mr-1">信贷关联</span>
              {article.credit_relevance}
            </p>
          )}
          {article.risk_transmission && (
            <p className="text-[11px] leading-[1.55] text-ink-tertiary line-clamp-1">
              <span className="font-semibold text-ink-secondary mr-1">风险传导</span>
              {article.risk_transmission}
            </p>
          )}
        </div>
      )}

      {/* Keywords */}
      {article.keywords && article.keywords.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {article.keywords.slice(0, 5).map((kw) => (
            <span
              key={kw}
              className="inline-flex items-center text-[10px] leading-none tracking-wide px-1.5 py-[3px] rounded bg-surface-sunken dark:bg-surface-sunken text-ink-tertiary select-none border border-border"
            >
              <span className="text-ink-faint mr-0.5">#</span>
              {kw}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
